// src/lib/sentry.ts
// Sentry SDK initialization for error monitoring and performance tracing.
// Call initSentry() once at app startup (before React renders) in main.tsx.
// Exposes the Sentry namespace for direct usage (captureException, setUser, etc.).
// Replays are enabled for 10% of sessions and 100% of sessions where an error occurs.
// Source maps are uploaded at build time via @sentry/vite-plugin (see vite.config.ts).
// Updated May 2026: ignoreErrors extended to suppress non-actionable console noise:
//   - Stale refresh token AuthApiErrors (400 on /token endpoint)
//   - WebContainer navigation errors (environment-level, not application code)
//   - Browser extension runtime.lastError messages
// Fixed May 2026: beforeSend — event.breadcrumbs is a Breadcrumb[] in memory (not
//   {values:[]}). Accessing .values returned Array.prototype.values (a function),
//   causing .some() to throw a TypeError that swallowed the original error in Sentry.
//   Also wrapped beforeSend body in try/catch to prevent future regressions.

import * as Sentry from '@sentry/react';

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;
const IS_PROD = import.meta.env.PROD as boolean;

export function initSentry(): void {
  if (!SENTRY_DSN) return;

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: IS_PROD ? 'production' : 'development',
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    tracesSampleRate: IS_PROD ? 0.2 : 1.0,
    replaysSessionSampleRate: IS_PROD ? 0.1 : 1.0,
    replaysOnErrorSampleRate: 1.0,
    ignoreErrors: [
      // Stale Supabase refresh token — handled gracefully by AuthContext
      'Invalid Refresh Token',
      'Refresh Token Not Found',
      'refresh_token_not_found',
      'refresh_token_already_used',
      // WebContainer / Bolt environment navigation error — not application code
      'Cannot navigate to URL',
      // Browser extension async message channel errors — not application code
      'A listener indicated an asynchronous response by returning true',
    ],
    beforeSend(event) {
      try {
        // Drop events caused by a 400 on the Supabase token refresh endpoint.
        // event.request.url is the page URL, not the failed network request URL,
        // so we inspect breadcrumbs and the exception message instead.
        // Note: event.breadcrumbs is a Breadcrumb[] in memory — NOT {values: []}.
        const tokenEndpointPattern = /\/auth\/v1\/token.*grant_type=refresh_token/;

        const breadcrumbs = Array.isArray(event.breadcrumbs) ? event.breadcrumbs : [];
        const hasBreadcrumbMatch = breadcrumbs.some(
          (crumb) =>
            (crumb.category === 'http' || crumb.category === 'fetch' || crumb.category === 'xhr') &&
            typeof crumb.data?.url === 'string' &&
            tokenEndpointPattern.test(crumb.data.url)
        );

        const exceptionValue = event.exception?.values?.[0]?.value ?? '';
        const hasExceptionMatch =
          exceptionValue.includes('/auth/v1/token') ||
          exceptionValue.includes('grant_type=refresh_token');

        if (hasBreadcrumbMatch || hasExceptionMatch) {
          return null;
        }
        return event;
      } catch {
        // If beforeSend itself throws, let the original event through
        // rather than swallowing it with an internal error.
        return event;
      }
    },
  });
}

export { Sentry };
